import { useState, useEffect, useCallback, useRef } from "react";
import formatTime from "../utils/formatTime";

/**
 * 自定义Hook，提供一个计时器功能
 * @param {number} initialTime - 初始时间（秒）
 * @param {boolean} autoStart - 是否自动开始计时
 * @param {boolean} countdown - 倒计时初始时间（秒）
 * @returns {Object} - 计时器状态和控制函数
 */
const useTimer = (initialTime = 0, autoStart = false, countdown = 0) => {
  const [time, setTime] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(autoStart);
  const intervalIdRef = useRef(null);

  // 开始计时
  const start = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
    }
  }, [isRunning]);

  // 暂停计时
  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  // 重置计时器
  const reset = useCallback(() => {
    setTime(initialTime);
    setIsRunning(false);
  }, [initialTime]);

  // 设置新时间
  const setNewTime = useCallback((newTime) => {
    setTime(newTime);
  }, []);

  // 处理计时逻辑
  useEffect(() => {
    if (isRunning) {
      const id = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
      intervalIdRef.current = id;
    } else if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    // 清理函数
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [isRunning]);

  // 格式化时间为 HH:MM:SS
  const formatedTime = formatTime(time);

  // 倒计时
  const currentCountdown = countdown - time < 0 ? 0 : countdown - time;
  const formatedCountdown = formatTime(currentCountdown);

  return {
    time, // 当前时间（秒）
    currentCountdown, // 倒计时剩余时间（秒）
    isRunning, // 计时器是否运行中
    start, // 开始函数
    pause, // 暂停函数
    reset, // 重置函数
    setTime: setNewTime, // 设置新时间函数
    formatedTime, // 格式化时间
    formatedCountdown, // 格式化倒计时
  };
};

export default useTimer;
